import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Lightning, ArrowRight } from "@phosphor-icons/react";
import GalaxyBackground from "../components/GalaxyBackground";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen bg-forge-bg">
      <div className="relative hidden md:block noise-overlay overflow-hidden border-r border-forge-border">
        <div className="absolute inset-0"><GalaxyBackground /></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2">
            <Lightning size={26} weight="fill" className="text-forge-primary" />
            <span className="font-mono text-lg tracking-tight text-white">Matrix</span>
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-white leading-tight">
              Build apps at<br />the speed of thought.
            </h1>
            <p className="mt-4 text-forge-primary font-mono text-sm tracking-tight">
              // describe it. ship it. own it.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="login-form">
          <div className="flex items-center gap-2 md:hidden mb-8">
            <Lightning size={24} weight="fill" className="text-forge-primary" />
            <span className="font-mono text-lg text-white">Matrix</span>
          </div>
          <p className="uppercase text-xs tracking-[0.2em] font-medium text-forge-primary mb-2">Welcome back</p>
          <h2 className="text-2xl font-medium tracking-tight text-white mb-8">Sign in to your workspace</h2>

          {error && (
            <div className="mb-4 border border-destructive/50 bg-destructive/10 text-red-300 text-sm px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <label className="block uppercase text-xs tracking-[0.2em] text-neutral-300 mb-2">Email</label>
          <input
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full bg-forge-surface border border-forge-border px-3 py-2.5 text-sm text-white rounded-sm mb-5 focus:outline-none focus:ring-1 focus:ring-forge-primary focus:border-forge-primary transition-colors"
            placeholder="you@example.com"
          />
          <label className="block uppercase text-xs tracking-[0.2em] text-neutral-300 mb-2">Password</label>
          <input
            data-testid="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full bg-forge-surface border border-forge-border px-3 py-2.5 text-sm text-white rounded-sm mb-6 focus:outline-none focus:ring-1 focus:ring-forge-primary focus:border-forge-primary transition-colors"
            placeholder="••••••••"
          />
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-forge-primary text-black font-medium py-2.5 rounded-sm flex items-center justify-center gap-2 hover:bg-yellow-400 transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in..." : "Sign in"} <ArrowRight size={16} weight="bold" />
          </button>
          <p className="mt-6 text-sm text-neutral-400">
            No account?{" "}
            <Link to="/register" className="text-forge-primary hover:underline" data-testid="go-register">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
